import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    FiAlertTriangle,
    FiCopy,
    FiDownload,
    FiCheck,
    FiArrowRight,
    FiUnlock,
    FiX
} from 'react-icons/fi';
import { seedPhraseManager } from '../../lib/seedPhraseManager';
import { useAuth } from '../../contexts/AuthContext';

interface CreateAccountFlowProps {
    onClose: () => void;
}

type Step = 'intro' | 'display' | 'verify' | 'success';

const CreateAccountFlow = ({ onClose }: CreateAccountFlowProps) => {
    const { loginWithSeed } = useAuth();
    const [step, setStep] = useState<Step>('intro');
    const [mnemonic, setMnemonic] = useState('');
    const [words, setWords] = useState<string[]>([]);
    const [testIndices, setTestIndices] = useState<number[]>([]);
    const [testInputs, setTestInputs] = useState<string[]>(['', '', '']);
    const [isCopied, setIsCopied] = useState(false);

    useEffect(() => {
        const newMnemonic = seedPhraseManager.generateMnemonic();
        setMnemonic(newMnemonic);
        setWords(newMnemonic.split(' '));
    }, []);

    const handleCopy = () => {
        navigator.clipboard.writeText(mnemonic);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleDownload = () => {
        const element = document.createElement("a");
        const file = new Blob([`MAVILILER SOZLUK - YEDEK\n\n12 Kelimeniz:\n\n${mnemonic}\n\nUYARI: Bu kelimeleri kimseyle paylasmayin!`], { type: 'text/plain' });
        element.href = URL.createObjectURL(file);
        element.download = "mavililer-sozluk-yedek.txt";
        document.body.appendChild(element);
        element.click();
    };

    const startVerification = () => {
        const indices: number[] = [];
        while (indices.length < 3) {
            const r = Math.floor(Math.random() * 12);
            if (!indices.includes(r)) indices.push(r);
        }
        setTestIndices(indices.sort((a, b) => a - b));
        setStep('verify');
    };

    const checkVerification = () => {
        const isValid = testIndices.every((wordIdx, testIdx) => {
            return testInputs[testIdx].trim().toLowerCase() === words[wordIdx];
        });

        if (isValid) {
            setStep('success');
        } else {
            alert('Bazı kelimeler hatalı. Lütfen tekrar kontrol edin.');
        }
    };

    const finalize = () => {
        loginWithSeed(mnemonic);
        onClose();
    };

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-white dark:bg-surface-900 border border-surface-200 dark:border-surface-800 w-full max-w-lg rounded-3xl overflow-hidden shadow-2xl relative"
            >
                <button
                    onClick={onClose}
                    className="absolute top-6 right-6 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-white transition-colors"
                >
                    <FiX className="text-xl" />
                </button>

                <div className="p-8">
                    <AnimatePresence mode="wait">
                        {step === 'intro' && (
                            <motion.div
                                key="intro"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="space-y-6"
                            >
                                <div className="w-16 h-16 bg-mavi-500/10 dark:bg-mavi-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <FiUnlock className="text-3xl text-mavi-500" />
                                </div>
                                <div className="text-center">
                                    <h2 className="text-2xl font-poppins font-bold text-gray-900 dark:text-white mb-2">Yeni Hesap Oluştur</h2>
                                    <p className="text-gray-500 dark:text-surface-400 font-merriweather text-sm">
                                        Mavililer Sözlük'e hoş geldiniz. Burada hesaplar 12 özel kelime ile korunur.
                                    </p>
                                </div>

                                <div className="bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 p-4 rounded-2xl flex gap-4">
                                    <FiAlertTriangle className="text-2xl text-orange-500 shrink-0" />
                                    <p className="text-xs text-orange-800 dark:text-orange-200/80 font-merriweather leading-relaxed">
                                        Bu 12 kelime senin hem şifren hem de anahtarındır. Kaybedersen hesabını asla geri alamazsın.
                                    </p>
                                </div>

                                <button
                                    onClick={() => setStep('display')}
                                    className="w-full bg-mavi-500 hover:bg-mavi-600 text-white font-poppins font-bold py-4 rounded-xl transition-all flex items-center justify-center gap-2 group shadow-lg shadow-mavi-500/20"
                                >
                                    Kelimelerimi Göster
                                    <FiArrowRight className="group-hover:translate-x-1 transition-transform" />
                                </button>
                            </motion.div>
                        )}

                        {step === 'display' && (
                            <motion.div
                                key="display"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="space-y-6"
                            >
                                <div className="text-center mb-6">
                                    <h2 className="text-xl font-poppins font-bold text-gray-900 dark:text-white mb-1">Kelimelerini Güvenle Kaydet</h2>
                                    <p className="text-xs text-gray-500 dark:text-surface-400 font-merriweather">Kelimeleri sırasıyla kağıda not alman en güvenli yoldur.</p>
                                </div>

                                <div className="grid grid-cols-3 gap-3">
                                    {words.map((word, i) => (
                                        <div key={i} className="bg-surface-50 dark:bg-surface-950 border border-surface-200 dark:border-surface-800 p-3 rounded-xl flex flex-col items-center shadow-inner">
                                            <span className="text-[10px] text-gray-400 dark:text-surface-600 uppercase font-poppins font-black mb-1">{i + 1}</span>
                                            <span className="text-gray-900 dark:text-white font-mono font-medium">{word}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="flex gap-4">
                                    <button
                                        onClick={handleCopy}
                                        className="flex-1 bg-surface-100 dark:bg-surface-800 hover:bg-surface-200 dark:hover:bg-surface-700 text-gray-700 dark:text-surface-200 py-3 rounded-xl flex items-center justify-center gap-2 transition-colors font-poppins font-bold text-xs"
                                    >
                                        {isCopied ? <FiCheck className="text-green-500" /> : <FiCopy />}
                                        {isCopied ? 'Kopyalandı' : 'Kopyala'}
                                    </button>
                                    <button
                                        onClick={handleDownload}
                                        className="flex-1 bg-surface-100 dark:bg-surface-800 hover:bg-surface-200 dark:hover:bg-surface-700 text-gray-700 dark:text-surface-200 py-3 rounded-xl flex items-center justify-center gap-2 transition-colors font-poppins font-bold text-xs"
                                    >
                                        <FiDownload />
                                        İndir (.txt)
                                    </button>
                                </div>

                                <button
                                    onClick={startVerification}
                                    className="w-full bg-mavi-500 hover:bg-mavi-600 text-white font-poppins font-bold py-4 rounded-xl transition-all shadow-lg shadow-mavi-500/20"
                                >
                                    Kaydettim, Devam Et
                                </button>
                            </motion.div>
                        )}

                        {step === 'verify' && (
                            <motion.div
                                key="verify"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                className="space-y-6"
                            >
                                <div className="text-center">
                                    <h2 className="text-xl font-poppins font-bold text-gray-900 dark:text-white mb-2">Hadi Test Edelim</h2>
                                    <p className="text-xs text-gray-500 dark:text-surface-400 font-merriweather">
                                        Doğru kaydettiğinden emin olmak için boşlukları doldur.
                                    </p>
                                </div>

                                <div className="space-y-4">
                                    {testIndices.map((wordIdx, testIdx) => (
                                        <div key={wordIdx} className="flex flex-col gap-2">
                                            <label className="text-[10px] font-poppins font-black text-gray-400 dark:text-surface-500 uppercase tracking-widest px-1">
                                                {wordIdx + 1}. Kelime
                                            </label>
                                            <input
                                                type="text"
                                                value={testInputs[testIdx]}
                                                onChange={(e) => {
                                                    const newInputs = [...testInputs];
                                                    newInputs[testIdx] = e.target.value;
                                                    setTestInputs(newInputs);
                                                }}
                                                className="bg-surface-50 dark:bg-surface-950 border border-surface-200 dark:border-surface-800 rounded-xl p-4 text-gray-900 dark:text-white focus:outline-none focus:border-mavi-500 transition-colors font-mono text-sm shadow-inner"
                                                placeholder="..."
                                            />
                                        </div>
                                    ))}
                                </div>

                                <div className="flex gap-4">
                                    <button
                                        onClick={() => setStep('display')}
                                        className="bg-surface-100 dark:bg-surface-800 hover:bg-surface-200 dark:hover:bg-surface-700 text-gray-700 dark:text-surface-200 px-6 rounded-xl transition-colors font-poppins font-bold text-xs"
                                    >
                                        Geri
                                    </button>
                                    <button
                                        onClick={checkVerification}
                                        className="flex-1 bg-mavi-500 hover:bg-mavi-600 text-white font-poppins font-bold py-4 rounded-xl transition-all shadow-lg shadow-mavi-500/20"
                                    >
                                        Doğrula ve Bitir
                                    </button>
                                </div>
                            </motion.div>
                        )}

                        {step === 'success' && (
                            <motion.div
                                key="success"
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="text-center py-8 space-y-6"
                            >
                                <div className="w-20 h-20 bg-green-500/10 dark:bg-green-500/20 rounded-full flex items-center justify-center mx-auto">
                                    <FiCheck className="text-4xl text-green-500" />
                                </div>
                                <div className="space-y-2">
                                    <h2 className="text-2xl font-poppins font-bold text-gray-900 dark:text-white">Harika!</h2>
                                    <p className="text-gray-500 dark:text-surface-400 font-merriweather text-sm">
                                        Artık Mavililer Sözlük'ün bir parçasısın.
                                    </p>
                                </div>
                                <button
                                    onClick={finalize}
                                    className="w-full bg-green-600 hover:bg-green-700 text-white font-poppins font-bold py-4 rounded-xl transition-all shadow-lg shadow-green-500/20"
                                >
                                    Sözlük'e Giriş Yap
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div className="p-4 bg-surface-50 dark:bg-surface-950 border-t border-surface-100 dark:border-surface-800 text-center">
                    <button
                        onClick={onClose}
                        className="text-xs font-poppins font-bold text-gray-400 hover:text-gray-600 dark:hover:text-surface-300 transition-colors uppercase tracking-widest"
                    >
                        Vazgeç ve Kapat
                    </button>
                </div>
            </motion.div>
        </div>
    );
};

export default CreateAccountFlow;
